import React, { useState } from 'react';
import { Disclosure, Switch } from '@headlessui/react';
import { ChevronDownIcon, LockClosedIcon, PlusIcon, AdjustmentsHorizontalIcon, QueueListIcon } from '@heroicons/react/20/solid';
import {
  UserIcon,
  BuildingOfficeIcon,
  BriefcaseIcon,
  MapPinIcon,
  PhoneIcon,
  DocumentDuplicateIcon,
  UsersIcon,
  TagIcon,
  ShoppingCartIcon,
  BanknotesIcon,
  ArrowPathIcon,
  CurrencyDollarIcon,
  CpuChipIcon,
  UserGroupIcon,
  CalendarDaysIcon,
  ClockIcon,
  BuildingLibraryIcon,
  ChartBarIcon,
  IdentificationIcon,
  ClipboardDocumentIcon
} from '@heroicons/react/24/outline';
import { DInput } from '../../../components/ui/Dashboard/DInput';
import { DButton } from '../../../components/ui/Dashboard/DButton';
import { DSelect, DSelectOption } from '../../../components/ui/Dashboard/DSelect';


interface FilterSectionProps {
  title: string;
  icon: React.ElementType;
  isLocked?: boolean;
  defaultOpen?: boolean;
  children: React.ReactNode;
}

function FilterSection({ title, icon: Icon, isLocked = false, defaultOpen = false, children }: FilterSectionProps) {
  return (
    <Disclosure defaultOpen={defaultOpen}>
      {({ open }) => (
        <div className="border-b border-slate-200">
          <Disclosure.Button className="flex w-full items-center justify-between py-3 px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors">
            <div className="flex items-center gap-3">
              <div className="w-5 h-5 flex items-center justify-center rounded text-brand-700 bg-brand-50">
                <Icon className="h-3.5 w-3.5" />
              </div>
              <span>{title}</span>
            </div>
            <div className="flex items-center gap-2">
              {isLocked && <LockClosedIcon className="h-4 w-4 text-slate-400" />}
              <ChevronDownIcon className={`h-4 w-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
            </div>
          </Disclosure.Button>
          <Disclosure.Panel className="px-4 pb-4 pt-2">
            {children}
          </Disclosure.Panel>
        </div>
      )}
    </Disclosure>
  );
}

const departmentOptions: DSelectOption[] = [
  { id: '1', name: 'Engineering' },
  { id: '2', name: 'Sales' },
  { id: '3', name: 'Marketing' },
  { id: '4', name: 'Human Resources' },
];

const seniorityOptions: DSelectOption[] = [
  { id: '1', name: 'C-Level' },
  { id: '2', name: 'VP' },
  { id: '3', name: 'Director' },
  { id: '4', name: 'Manager' },
];

export default function FilterSidebar() {
  const [exactMatch, setExactMatch] = useState(false);
  const [mustIncludeTech, setMustIncludeTech] = useState(false);
  const [jobTitleTab, setJobTitleTab] = useState('normal');
  const [locationTab, setLocationTab] = useState('people');
  const [selectedDept, setSelectedDept] = useState<DSelectOption>();
  const [selectedSeniority, setSelectedSeniority] = useState<DSelectOption>();

  return (
    <div className="flex h-full w-full flex-col bg-white">
      <div className="flex-1 overflow-y-auto">
        <FilterSection title="Person Name" icon={UserIcon}>
          <DInput placeholder="Type a name and press Enter" />
        </FilterSection>

        <FilterSection title="Company" icon={BuildingOfficeIcon}>
          <div className="space-y-3">
            <DButton variant="outline" className="w-full justify-start font-medium text-slate-600 border-slate-200 shadow-none hover:bg-slate-50">
              <QueueListIcon className="h-4 w-4 mr-2 text-slate-400" />
              Bulk include/exclude companies
            </DButton>
            <DInput 
              placeholder="Search companies..." 
              rightIcon={<PlusIcon className="h-5 w-5 hover:text-brand-600 cursor-pointer transition-colors" />} 
            />
          </div>
        </FilterSection>

        <FilterSection title="Job Title" icon={BriefcaseIcon}>
          <div className="space-y-4">
            <div className="flex rounded-md bg-slate-100 p-1">
              <button
                className={`flex-1 rounded px-3 py-1.5 text-xs font-semibold transition-all ${jobTitleTab === 'normal' ? 'bg-white shadow-sm text-brand-700' : 'text-slate-600 hover:text-slate-900'}`}
                onClick={() => setJobTitleTab('normal')}
              >
                Normal search
              </button>
              <button
                className={`flex-1 rounded px-3 py-1.5 text-xs font-semibold transition-all ${jobTitleTab === 'boolean' ? 'bg-white shadow-sm text-brand-700' : 'text-slate-600 hover:text-slate-900'}`}
                onClick={() => setJobTitleTab('boolean')}
              >
                Boolean search
              </button>
            </div>

            <DInput 
              placeholder="Search job titles..." 
              rightIcon={
                <div className="flex gap-1.5">
                  <AdjustmentsHorizontalIcon className="h-4 w-4 hover:text-brand-600 cursor-pointer transition-colors" />
                  <QueueListIcon className="h-4 w-4 hover:text-brand-600 cursor-pointer transition-colors" />
                </div>
              } 
            />

            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate-700">Match only exact job titles</span>
              <Switch
                checked={exactMatch}
                onChange={setExactMatch}
                className={`${exactMatch ? 'bg-brand-600' : 'bg-slate-200'} relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none`}
              >
                <span className={`${exactMatch ? 'translate-x-5' : 'translate-x-1'} inline-block h-3 w-3 transform rounded-full bg-white transition-transform shadow-sm`} />
              </Switch>
            </div>

            <DSelect 
              options={departmentOptions} 
              value={selectedDept} 
              onChange={setSelectedDept} 
              placeholder="Departments" 
            />

            <DSelect 
              options={seniorityOptions} 
              value={selectedSeniority} 
              onChange={setSelectedSeniority} 
              placeholder="Seniority" 
            />
          </div>
        </FilterSection>

        <FilterSection title="Location" icon={MapPinIcon}>
          <div className="space-y-3">
            <div className="flex rounded-md border border-slate-200 p-0.5 bg-slate-50">
              <button
                className={`flex flex-1 items-center justify-center gap-2 rounded px-3 py-2 text-xs font-semibold transition-all ${locationTab === 'people' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-900'}`}
                onClick={() => setLocationTab('people')}
              >
                <UserIcon className="h-3.5 w-3.5" />
                People
              </button>
              <button
                className={`flex flex-1 items-center justify-center gap-2 rounded px-3 py-2 text-xs font-semibold transition-all ${locationTab === 'companies' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-900'}`}
                onClick={() => setLocationTab('companies')}
              >
                <BuildingOfficeIcon className="h-3.5 w-3.5" />
                Companies HQ
              </button>
            </div>
            <DInput placeholder="Enter location" />
          </div>
        </FilterSection>

        <FilterSection title="Contact Details" icon={PhoneIcon}>
          <div className="text-sm text-slate-500 p-2">Select contact requirements</div>
        </FilterSection>

        <FilterSection title="Duplicate Control" icon={DocumentDuplicateIcon} isLocked>
          <div className="text-sm text-slate-500 p-2">Premium feature</div>
        </FilterSection>

        <FilterSection title="Employee Headcount" icon={UsersIcon}>
          <div className="text-sm text-slate-500 p-2">Select headcount range</div>
        </FilterSection>

        <FilterSection title="Industry & Keyword" icon={TagIcon}>
          <div className="text-sm text-slate-500 p-2">Search industries</div>
        </FilterSection>

        <FilterSection title="Buying Intent" icon={ShoppingCartIcon}>
          <div className="text-sm text-slate-500 p-2">Select intent topics</div>
        </FilterSection>

        <FilterSection title="Revenue" icon={BanknotesIcon} isLocked>
          <div className="text-sm text-slate-500 p-2">Premium feature</div>
        </FilterSection>

        <FilterSection title="Job Change" icon={ArrowPathIcon}>
          <div className="text-sm text-slate-500 p-2">Filter by recent job changes</div>
        </FilterSection>

        <FilterSection title="Funding" icon={CurrencyDollarIcon} isLocked>
          <div className="text-sm text-slate-500 p-2">Premium feature</div>
        </FilterSection>

        <FilterSection title="Technologies" icon={CpuChipIcon} isLocked>
          <div className="space-y-4">
            <DInput placeholder="Enter technologies" />
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate-700">Must include all selected technologies</span>
              <Switch
                checked={mustIncludeTech}
                onChange={setMustIncludeTech}
                className={`${mustIncludeTech ? 'bg-brand-600' : 'bg-slate-200'} relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none`}
              >
                <span className={`${mustIncludeTech ? 'translate-x-5' : 'translate-x-1'} inline-block h-3 w-3 transform rounded-full bg-white transition-transform shadow-sm`} />
              </Switch>
            </div>
            <div className="text-xs text-slate-400 text-right">Powered by Wappalyzer</div>
          </div>
        </FilterSection>

        <FilterSection title="Select Per Company" icon={UserGroupIcon}>
           <div className="space-y-3">
            <p className="text-xs text-slate-500 leading-relaxed">
              This filter allows you to select how many employees per company to display in your search results.
            </p>
            <DInput type="number" placeholder="Enter max people per company" />
          </div>
        </FilterSection>

        <FilterSection title="Total Years of Experience" icon={CalendarDaysIcon}>
          <div className="text-sm text-slate-500 p-2">Select experience range</div>
        </FilterSection>

        <FilterSection title="Time in Current Role" icon={ClockIcon}>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1.5">Min</label>
              <div className="flex gap-2">
                <DInput type="number" placeholder="Years" />
                <DInput type="number" placeholder="Months" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1.5">Max</label>
              <div className="flex gap-2">
                <DInput type="number" placeholder="Years" />
                <DInput type="number" placeholder="Months" />
              </div>
            </div>
          </div>
        </FilterSection>

        <FilterSection title="Time in Current Company" icon={ClockIcon}>
          <div className="text-sm text-slate-500 p-2">Select duration</div>
        </FilterSection>

        <FilterSection title="Founded Year" icon={BuildingLibraryIcon}>
          <div className="text-sm text-slate-500 p-2">Select year range</div>
        </FilterSection>

        <FilterSection title="Headcount Growth" icon={ChartBarIcon}>
          <div className="text-sm text-slate-500 p-2">Select growth percentage</div>
        </FilterSection>

        <FilterSection title="Company Attributes" icon={IdentificationIcon}>
          <div className="space-y-2">
            <span className="text-sm font-medium text-slate-700">Attributes</span>
            <div className="space-y-2.5 pt-1">
              {['Offers demo', 'Offers free trial', 'Is downloadable', 'Is/Has a mobile app', 'Has online reviews', 'Has public pricing'].map((attr) => (
                <label key={attr} className="flex items-center gap-3 cursor-pointer group">
                  <DInput 
                    type="checkbox" 
                    className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500 transition-colors cursor-pointer" 
                  />
                  <span className="text-sm text-slate-600 group-hover:text-slate-900 transition-colors">{attr}</span>
                </label>
              ))}
            </div>
          </div>
        </FilterSection>

        <FilterSection title="Job Posting" icon={ClipboardDocumentIcon} isLocked>
          <div className="text-sm text-slate-500 p-2">Premium feature</div>
        </FilterSection>
      </div>
    </div>
  );
}